import {
	InnerBlocks,
	InspectorControls,
	useBlockProps,
	RichText,
	useInnerBlocksProps,
	store as blockEditorStore,
	MediaUpload,
} from '@wordpress/block-editor';
import {
	Button,
	PanelBody,
	SelectControl,
	TextControl,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { sprintf, __ } from '@wordpress/i18n';

function ColumnEdit( {
	attributes: {
		templateLock = false,
		allowedBlocks,
		style,
		tabScreenIndex,
		tabAnswerType,
		tabAnswerAudio,
		tabAnswerVideo,
		tabAnswerText,
	},
	setAttributes,
	clientId,
} ) {
	const { columnsIds, hasChildBlocks } = useSelect(
		( select ) => {
			const { getBlockOrder, getBlockRootClientId } =
				select( blockEditorStore );

			const rootId = getBlockRootClientId( clientId );

			return {
				hasChildBlocks: getBlockOrder( clientId ).length > 0,
				rootClientId: rootId,
				columnsIds: getBlockOrder( rootId ),
			};
		},
		[ clientId ]
	);

	const blockProps = useBlockProps( {
		className: 'block-core-columns',
		style,
		tabScreenIndex,
	} );

	const columnsCount = columnsIds.length;
	const currentColumnPosition = columnsIds.indexOf( clientId ) + 1;

	const labels = sprintf(
		__( '%1$s (%2$d of %3$d)', 'ask-faq' ),
		blockProps[ 'aria-label' ],
		currentColumnPosition,
		columnsCount
	);

	const innerBlocksProps = useInnerBlocksProps(
		{ ...blockProps, 'aria-label': labels, style },
		{
			templateLock,
			allowedBlocks,
			renderAppender: hasChildBlocks
				? undefined
				: InnerBlocks.ButtonBlockAppender,
		}
	);

	const onChangeTabAnswerType = ( newType ) => {
		setAttributes( { tabAnswerType: newType } );
	};
	const onChangeTabAnswerText = ( newText ) => {
		setAttributes( { tabAnswerText: newText } );
	};
	const onSelectAudioMediaChange = ( media ) => {
		setAttributes( {
			tabAnswerAudio: media.url,
		} );
	};
	const onSelectVideoMediaChange = ( media ) => {
		setAttributes( {
			tabAnswerVideo: media.url,
		} );
	};

	const tabAnswerTypeOptions = [
		{ value: 'text', label: __( 'Text', 'ask-faq' ) },
		{ value: 'audio', label: __( 'Audio', 'ask-faq' ) },
		{ value: 'video', label: __( 'Video', 'ask-faq' ) },
	];

	return (
		<>
			<InspectorControls>
				<PanelBody>
					<SelectControl
						label={ __( 'Type of Write Answer', 'ask-faq' ) }
						value={ tabAnswerType }
						options={ tabAnswerTypeOptions.map(
							( { value, label } ) => ( {
								value,
								label,
							} )
						) }
						onChange={ onChangeTabAnswerType }
						help={ __(
							'Choose the type of answer you wanna show',
							'ask-faq'
						) }
					/>
					{ tabAnswerType === 'text' && (
						<TextControl
							label={ __( 'Write Answer', 'ask-faq' ) }
							value={ tabAnswerText }
							onChange={ onChangeTabAnswerText }
							placeholder={ __(
								'Write Answer',
								'ask-faq'
							) }
						/>
					) }
					{ tabAnswerType === 'audio' && (
						<MediaUpload
						onSelect={ onSelectAudioMediaChange }
						allowedTypes={ [ 'audio' ] }
						className="ask__audio audio-player"
						render={ ( { open } ) => (
							<Button
								onClick={ open }
								style={ {
									fontSize: '16px',
								} }
							>
								<span className="dashicons dashicons-cloud-upload"></span>{ ' ' }
								{ __(
									'Upload Audio',
									'ask-faq'
								) }
							</Button>
						) }
					/>
					) }
					{ tabAnswerType === 'video' && (
						<MediaUpload
						onSelect={ onSelectVideoMediaChange }
						allowedTypes={ [ 'video' ] }
						render={ ( { open } ) => (
							<Button
								onClick={ open }
								style={ {
									fontSize: '16px',
								} }
							>
								<span className="dashicons dashicons-cloud-upload"></span>{ ' ' }
								{ __(
									'Upload Video',
									'ask-faq'
								) }
							</Button>
						) }
					/>
					) }
				</PanelBody>
			</InspectorControls>
			<div { ...innerBlocksProps }>
				{ ( () => {
					if ( tabAnswerType === 'text' ) {
						return (
							<RichText
								onChange={ onChangeTabAnswerText }
								className="ask__tab__question--text"
								value={ tabAnswerText }
								placeholder={ __(
									'Add Answer',
									'ask-faq'
								) }
								tagName="p"
								allowedFormats={ [] }
							/>
						);
					} else if ( tabAnswerType === 'audio' ) {
						return (
							<div className="ask__tab__question--text">
								<MediaUpload
									onSelect={ onSelectAudioMediaChange }
									allowedTypes={ [ 'audio' ] }
									render={ ( { open } ) => (
										<Button
											onClick={ open }
											style={ {
												fontSize: '16px',
											} }
										>
											<span className="dashicons dashicons-cloud-upload"></span>{ ' ' }
											{ __(
												'Upload Audio',
												'ask-faq'
											) }
										</Button>
									) }
								/>
								{ tabAnswerAudio === '' ? (
									<>
										{ __(
											'No Audio Uploaded Yet',
											'ask-faq'
										) }
									</>
								) : (
									<audio
										controls
										src={ tabAnswerAudio }
									></audio>
								) }
							</div>
						);
					} else if ( tabAnswerType === 'video' ) {
						return (
							<div className="ask__tab__question--text">
								<MediaUpload
									onSelect={ onSelectVideoMediaChange }
									allowedTypes={ [ 'video' ] }
									render={ ( { open } ) => (
										<Button
											onClick={ open }
											style={ {
												fontSize: '16px',
											} }
										>
											<span className="dashicons dashicons-cloud-upload"></span>{ ' ' }
											{ __(
												'Upload Video',
												'ask-faq'
											) }
										</Button>
									) }
								/>
								{ tabAnswerVideo === '' ? (
									<>
										{ __(
											'No Video Uploaded Yet',
											'ask-faq'
										) }
									</>
								) : (
									<video width="320" height="240" controls>
										<source src={ tabAnswerVideo } />
									</video>
								) }
							</div>
						);
					}
				} )() }
			</div>
		</>
	);
}

export default ColumnEdit;
